import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { PremiumAmountDisplay } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Pricing/SolvingRatePricing/PremiumAmountDisplay.tsx';
import { PriceDisplay } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardPriceSummary/Pricing/SolvingRatePricing/PriceDisplay.tsx';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils.ts';
import { DEFAULT_CLIENTS_WAY } from '@/store/flexi-forward/state.ts';
import { useCurrencyPrecision } from '@/utils/currencies/useCurrencyPrecision.ts';

interface SolvingUpfrontPricingProps {
  isInternalSales: boolean;
}

export function SolvingRatePricing({ isInternalSales }: SolvingUpfrontPricingProps) {
  const { clientWay, currencyPair, currencies, notionalCurrencyIndex } = useFlexiForwardSelector((flexiForward) => {
    const currencyPair = flexiForward.properties.currencyPair ?? '';
    const currencies = getCurrenciesFromCurrencyPair(currencyPair);

    return {
      clientWay: flexiForward.properties?.clientsWay ?? DEFAULT_CLIENTS_WAY,
      currencyPair,
      currencies,
      notionalCurrencyIndex: flexiForward.properties.notionalCurrencyIndex ?? 1,
    };
  });

  const displayedClientsWay = useMemo(
    () => (notionalCurrencyIndex === 1 ? clientWay : clientWay === 'Buy' ? 'Sell' : 'Buy'),
    [clientWay, notionalCurrencyIndex],
  );

  const displayedCurrency = useMemo(
    () => (notionalCurrencyIndex === 1 ? currencies.currency1 : currencies.currency2),
    [notionalCurrencyIndex, currencies],
  );

  const precision = useCurrencyPrecision(currencyPair);

  return (
    <div className="d-flex flex-column justify-content-between h-100" data-e2e="solving-rate-pricing">
      {/* WAY ACTION */}
      <div className="text-nowrap fs-12 line-height-1 mx-1">
        <FormattedMessage
          id={`flexiForwardTile.rates.streaming.wayAction.${displayedClientsWay}`}
          values={{ isInternalSales, currency: displayedCurrency }}
        />
      </div>

      {/* PRICE */}
      <PriceDisplay precision={precision} />

      {/* PREMIUM AMOUNT */}
      <PremiumAmountDisplay currencies={currencies} isInternalSales={isInternalSales} />
    </div>
  );
}
